import { gql, useMutation, useQuery } from '@apollo/client';
import IconSandbox from '@apollo/icons/default/IconSandbox.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { ListItem } from '@apollo/space-kit/ListItem';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { groupBy, keyBy, orderBy, partition, sortBy } from 'lodash';
import React, { OptionHTMLAttributes, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import * as Yup from 'yup';

import { sandboxGraphRouteConfig } from 'src/app/graph/routes';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { collectionEntryStateToInput } from '../../explorerPage/helpers/collectionEntryStateToInput';
import {
  CollectionEntryIdState,
  CollectionEntryState,
  SharedCollectionEntryState,
} from '../../explorerPage/hooks/useExplorerState/useEditorTabState/collectionEntryState';
import { headersArrayFromString } from '../../explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../../explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { SANDBOX_SELECT_ID } from '../../explorerPage/operationCollections/OperationCollections';
import {
  NEW_DEFAULT_COLLECTION_OPTION_ID,
  useCollectionEntryNameValidation,
} from '../../explorerPage/operationCollections/operationCollectionsModals/sharedValidation';
import {
  GraphRef,
  graphRefToString,
  parseGraphRef,
} from '../../hooks/useGraphRef';

export const SaveCollectionEntryModal = ({
  title,
  graphRef,
  initialCollectionEntryName,
  operationState,
  onCollectionEntrySaved,
  isOpen,
  onClose,
}: {
  title: string;
  graphRef: GraphRef | null;
  initialCollectionEntryName: string;
  operationState: CollectionEntryState;
  onCollectionEntrySaved: (
    args: {
      collectionType: 'sandbox' | 'shared' | 'personal';
    } & CollectionEntryIdState &
      SharedCollectionEntryState,
  ) => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });
  const [
    lastUpdatedOperationCollectionId,
    setLastUpdatedOperationCollectionId,
  ] = usePerGraphIdentifierLocalStorage({
    key: 'lastUpdatedOperationCollectionId',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: '',
  });

  const { data, loading: dataLoading } = useQuery<
    GraphQLTypes.SaveCollectionEntryModalQuery,
    GraphQLTypes.SaveCollectionEntryModalQueryVariables
  >(
    gql`
      fragment SaveCollectionEntryModalQueryOperationCollectionFragment on OperationCollection {
        id
        name
        isShared
        isSandbox
        variants {
          id
        }
        permissions {
          canEditOperations
        }
      }

      fragment SaveCollectionEntryModalQueryCollectionsOnServiceFragment on Service {
        id
        title
        variants {
          id
          isFavoriteOfCurrentUser
          name
          operationCollections {
            id
            ...SaveCollectionEntryModalQueryOperationCollectionFragment
          }
        }
      }

      query SaveCollectionEntryModalQuery(
        $graphId: ID!
        $variantName: String!
        $hasGraphVariant: Boolean!
        $isSandbox: Boolean!
      ) {
        service(id: $graphId) @include(if: $hasGraphVariant) {
          id
          variant(name: $variantName) {
            id
            operationCollections {
              id
              ...SaveCollectionEntryModalQueryOperationCollectionFragment
            }
          }
          account {
            id
          }
          ...SaveCollectionEntryModalQueryCollectionsOnServiceFragment
        }
        me {
          id
          ... on User {
            sandboxOperationCollections {
              id
              ...SaveCollectionEntryModalQueryOperationCollectionFragment
            }
            memberships @include(if: $isSandbox) {
              user {
                id
              }
              account {
                id
                services {
                  id
                  ...SaveCollectionEntryModalQueryCollectionsOnServiceFragment
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef?.graphId ?? '',
        variantName: graphRef?.graphVariant ?? '',
        hasGraphVariant: !!graphRef,
        isSandbox: isSandboxRoute,
      },
    },
  );

  const [addOperationMutation] = useMutation<
    GraphQLTypes.AddOperation,
    GraphQLTypes.AddOperationVariables
  >(
    gql`
      mutation AddOperation(
        $collectionId: ID!
        $name: String!
        $operationInput: OperationCollectionEntryStateInput!
      ) {
        operationCollection(id: $collectionId) {
          addOperation(name: $name, operationInput: $operationInput) {
            ... on OperationCollectionEntry {
              id
              createdBy {
                id
                name
              }
              collection {
                id
                operations {
                  id
                }
                variants {
                  id
                  sharedHeaders
                }
              }
            }
            ... on Error {
              message
            }
          }
        }
      }
    `,
    { onError: (error) => setMutationErrorMessage(error.message) },
  );

  const [createDefaultCollection] = useMutation<
    GraphQLTypes.CreateDefaultOperationCollectionMutation,
    GraphQLTypes.CreateDefaultOperationCollectionMutationVariables
  >(
    gql`
      mutation CreateDefaultOperationCollectionMutation(
        $variantRefs: [ID!]
        $isSandbox: Boolean!
      ) {
        createOperationCollection(
          name: "My Collection"
          isShared: false
          variantRefs: $variantRefs
          isSandbox: $isSandbox
        ) {
          ... on OperationCollection {
            id
            isShared
            isSandbox
            variants {
              id
              operationCollections {
                id
              }
            }
            createdBy @include(if: $isSandbox) {
              id
              ... on User {
                sandboxOperationCollections {
                  id
                }
              }
            }
          }
          ... on Error {
            message
          }
        }
      }
    `,
    {
      onError: (error) => setMutationErrorMessage(error.message),
    },
  );

  const availableVariants = React.useMemo(() => {
    let unorderedVariants;
    if (isSandboxRoute) {
      if (data?.me?.__typename !== 'User' || !data.me.memberships) {
        return undefined;
      }
      unorderedVariants = data.me.memberships.flatMap((membership) =>
        membership.account.services.flatMap((graph) =>
          graph.variants.map((variant) => ({
            variantId: variant.id,
            graphId: graph.id,
            graphTitle: graph.title,
            orgId: membership.account.id,
            isFavorite: variant.isFavoriteOfCurrentUser,
            name: variant.name,
            operationCollections: variant.operationCollections,
          })),
        ),
      );
    } else {
      const graph = data?.service;
      const account = graph?.account;
      // If you are viewing a public variant you would get
      // a service with no account, but that can't happen on
      // a sandbox route anyways
      if (!graph || !account) {
        return undefined;
      }
      unorderedVariants = graph.variants.map((variant) => ({
        variantId: variant.id,
        graphId: graph.id,
        graphTitle: graph.title,
        orgId: account.id,
        isFavorite: variant.isFavoriteOfCurrentUser,
        name: variant.name,
        operationCollections: variant.operationCollections,
      }));
    }
    return orderBy(unorderedVariants, (variant) => variant.isFavorite, [
      'desc',
    ]);
  }, [isSandboxRoute, data]);

  // If user is on sandbox, the variant select will control sandboxSelectorState,
  // which is a local storage backed value that powers diffs/checks as well. On
  // a registered graph the variant select will control nonSandboxSelectorState,
  // a GraphRef in React state.
  const [sandboxSelectorState, setSandboxSelectorState] = useLocalStorage(
    'sandboxCheckDiffSelectorState',
  );
  const [graphRefSelectorState, setGraphRefSelectorState] = React.useState<
    GraphRef | undefined | null
  >(graphRef ?? undefined);

  const selectedGraphRef = useMemo(() => {
    if (isSandboxRoute) {
      const sandboxSelectorGraphVariant = sandboxSelectorState
        ? availableVariants?.find(
            (variant) => variant.variantId === sandboxSelectorState.variantId,
          )
        : undefined;
      return sandboxSelectorGraphVariant
        ? {
            graphVariant: sandboxSelectorGraphVariant.name,
            graphId: sandboxSelectorGraphVariant.graphId,
          }
        : undefined;
    } else {
      return graphRefSelectorState;
    }
  }, [
    isSandboxRoute,
    availableVariants,
    graphRefSelectorState,
    sandboxSelectorState,
  ]);
  const setSelectedGraphRef = React.useCallback(
    (newGraphRef: GraphRef | undefined | null) => {
      if (isSandboxRoute) {
        const matchingVariant = availableVariants?.find(
          (variant) =>
            variant.graphId === newGraphRef?.graphId &&
            variant.name === newGraphRef?.graphVariant,
        );
        setSandboxSelectorState(matchingVariant);
      } else {
        setGraphRefSelectorState(newGraphRef);
      }
    },
    [availableVariants, isSandboxRoute, setSandboxSelectorState],
  );

  const collections = selectedGraphRef
    ? availableVariants?.find(
        (variant) =>
          variant.graphId === selectedGraphRef.graphId &&
          variant.name === selectedGraphRef.graphVariant,
      )?.operationCollections
    : data?.me?.__typename === 'User'
      ? data.me.sandboxOperationCollections
      : undefined;

  const initialCollectionIdOrNew = useMemo(() => {
    const userEditableCollections = collections?.filter(
      (collection) => collection.permissions.canEditOperations,
    );

    return userEditableCollections?.length === 1
      ? userEditableCollections[0]?.id
      : lastUpdatedOperationCollectionId;
  }, [collections, lastUpdatedOperationCollectionId]);

  const [collectionIdOrNew, setCollectionIdOrNew] = useState(
    initialCollectionIdOrNew ?? '',
  );
  const {
    collectionEntryNameValidation,
    loading: collectionEntryNameValidationLoading,
  } = useCollectionEntryNameValidation({ collectionIdOrNew });
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        collectionEntryName: collectionEntryNameValidation,
        collectionIdOrNew: Yup.string().required('Collection is required'),
      }),
    [collectionEntryNameValidation],
  );

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    validateForm,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    initialValues: {
      collectionEntryName: initialCollectionEntryName,
      collectionIdOrNew: initialCollectionIdOrNew ?? '',
    },
    async onSubmit(formValues) {
      try {
        const collectionEntryInput =
          collectionEntryStateToInput(operationState);

        let collectionId: string;
        let collectionType: 'sandbox' | 'shared' | 'personal';

        if (formValues.collectionIdOrNew === NEW_DEFAULT_COLLECTION_OPTION_ID) {
          const printedGraphRef = graphRefToString(graphRef);
          const isSandbox = !graphRef;
          const createDefaultCollectionResult = (
            await createDefaultCollection({
              variables: {
                isSandbox,
                variantRefs: printedGraphRef ? [printedGraphRef] : [],
              },
            })
          ).data?.createOperationCollection;
          if (
            createDefaultCollectionResult?.__typename !== 'OperationCollection'
          ) {
            setMutationErrorMessage(
              createDefaultCollectionResult?.message ??
                'Could not create default collection',
            );
            return;
          }
          collectionId = createDefaultCollectionResult.id;
          collectionType = isSandbox ? 'sandbox' : 'personal';
        } else {
          collectionId = formValues.collectionIdOrNew;
          const collection = collectionsById[collectionId];
          collectionType = collection?.isSandbox
            ? 'sandbox'
            : collection?.isShared
              ? 'shared'
              : 'personal';
        }

        const result = (
          await addOperationMutation({
            variables: {
              collectionId,
              operationInput: collectionEntryInput,
              name: formValues.collectionEntryName,
            },
          })
        ).data?.operationCollection?.addOperation;
        if (result?.__typename !== 'OperationCollectionEntry') {
          setMutationErrorMessage(
            result?.message ?? 'Could not save operation',
          );
        } else {
          setLastUpdatedOperationCollectionId(collectionId);
          const sharedCollectionOriginVariant =
            !result.collection.variants.some(
              (variant) => variant.id === graphRefToString(graphRef),
            )
              ? result.collection.variants[0]
              : undefined;
          onCollectionEntrySaved({
            collectionId,
            collectionEntryId: result.id,
            collectionType,
            sharedCollectionOriginVariantRef:
              parseGraphRef(sharedCollectionOriginVariant?.id) ?? undefined,
            sharedCollectionOriginVariantHeaders:
              (graphRef &&
                headersArrayFromString(
                  sharedCollectionOriginVariant?.sharedHeaders ?? '',
                )) ??
              [],
          });
          onClose();
        }
      } catch {
        // Captured via `onError`
      }
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  // When collectionId changes, validate form 'blur' does not get called for
  // selects when the option is initially clicked, only when you actually
  // click off the field, which ends up being a really annoying experience
  React.useEffect(() => {
    validateForm();
  }, [values.collectionIdOrNew, validateForm]);

  // There is a bit of a weird order problem with the schema and the values, so
  // this sets a state value we can read in the schema
  React.useEffect(
    () => setCollectionIdOrNew(values.collectionIdOrNew),
    [values.collectionIdOrNew],
  );

  const [sandboxCollections, variantCollections] = partition(
    collections ?? [],
    (collection) => collection.isSandbox,
  );
  const [sharedCollections, personalCollections] = partition(
    variantCollections,
    (collection) => collection.isShared,
  );
  const collectionsById = keyBy(collections, (collection) => collection.id);
  const selectedCollection = collectionsById[values.collectionIdOrNew];

  const hasCollectionSelectError =
    (errors.collectionIdOrNew && touched.collectionIdOrNew) ||
    (errors.collectionEntryName && touched.collectionEntryName);

  const selectedVariant = selectedGraphRef
    ? availableVariants?.find(
        (variant) =>
          variant.graphId === selectedGraphRef.graphId &&
          variant.name === selectedGraphRef.graphVariant,
      )
    : undefined;

  const [mutationErrorMessage, setMutationErrorMessage] =
    React.useState<string>();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate={true}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {dataLoading ? (
              <Loading />
            ) : !collections ? (
              <div>Could not fetch collections to save to</div>
            ) : (
              <>
                <FormControl
                  isRequired
                  isInvalid={
                    !!errors.collectionEntryName &&
                    !!touched.collectionEntryName
                  }
                >
                  <FormLabel>Operation Name</FormLabel>
                  <Input
                    autoFocus
                    name="collectionEntryName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="YourOperationName"
                    value={values.collectionEntryName}
                    isDisabled={isSubmitting}
                  />
                  <FormErrorMessage>
                    {errors.collectionEntryName}
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={
                    !!errors.collectionIdOrNew && !!touched.collectionIdOrNew
                  }
                >
                  <FormLabel>
                    Which collection would you like to save this operation to?
                  </FormLabel>
                  <div
                    className={classnames(
                      'flex items-center rounded border p-1',
                      hasCollectionSelectError
                        ? 'border-error'
                        : 'border-primary',
                    )}
                  >
                    {availableVariants?.length !== 0 && (
                      <Select
                        feel="flat"
                        name="collectionVariant"
                        className="border-none"
                        onChange={(e) => {
                          const isSandboxSelected =
                            e.target.value === SANDBOX_SELECT_ID;
                          const newGraphRef = isSandboxSelected
                            ? undefined
                            : parseGraphRef(e.target.value);
                          setSelectedGraphRef(newGraphRef);
                        }}
                        renderTriggerNode={() => {
                          if (selectedVariant) {
                            return `${selectedVariant.graphTitle} / ${selectedVariant.name}`;
                          }
                          return (
                            <span className="flex items-center">
                              <IconSandbox className="mr-2" />
                              Sandbox
                            </span>
                          );
                        }}
                        value={
                          selectedGraphRef
                            ? graphRefToString(selectedGraphRef)
                            : SANDBOX_SELECT_ID
                        }
                      >
                        <option
                          value={SANDBOX_SELECT_ID}
                          key={SANDBOX_SELECT_ID}
                        >
                          <div className="flex items-center font-semibold uppercase">
                            <IconSandbox className="mr-2" /> Sandbox
                          </div>
                        </option>
                        {sortBy(
                          Object.entries(
                            groupBy(
                              availableVariants,
                              (variants) => variants.graphId,
                            ),
                          ),
                          (graphs) => graphs[0].toLowerCase(),
                        ).map(([graphId, graphVariants]) => (
                          <optgroup
                            label={graphVariants[0]?.graphTitle}
                            key={graphId}
                          >
                            {sortBy(graphVariants, (variant) =>
                              variant.name.toLowerCase(),
                            ).map((variantRef) => {
                              const refString = graphRefToString({
                                graphId: variantRef.graphId,
                                graphVariant: variantRef.name,
                              });
                              return (
                                <option value={refString} key={refString}>
                                  {variantRef.graphTitle} / {variantRef.name}
                                </option>
                              );
                            })}
                          </optgroup>
                        ))}
                      </Select>
                    )}
                    <Select
                      feel="flat"
                      renderTriggerNode={() => {
                        if (
                          values.collectionIdOrNew ===
                          NEW_DEFAULT_COLLECTION_OPTION_ID
                        ) {
                          return graphRef
                            ? 'Save to a new default personal collection'
                            : 'Save to a new default sandbox collection';
                        }
                        if (selectedCollection) {
                          return `${
                            selectedCollection.isSandbox
                              ? 'Sandbox'
                              : selectedCollection.isShared
                                ? 'Shared'
                                : 'Personal'
                          } / ${selectedCollection.name}`;
                        }
                        return 'Select a collection';
                      }}
                      className="border-none"
                      name="collectionIdOrNew"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      renderListItem={(itemProps) => {
                        const isDisabled = (
                          itemProps as OptionHTMLAttributes<HTMLOptionElement>
                        ).disabled;
                        return (
                          <ListItem
                            {...itemProps}
                            className={classnames(isDisabled && 'opacity-30')}
                            interactive={!isDisabled}
                          >
                            <Tooltip
                              label="You do not have permission to save operations in this collection."
                              disabled={!isDisabled}
                            >
                              <div>{itemProps.children}</div>
                            </Tooltip>
                          </ListItem>
                        );
                      }}
                    >
                      {!selectedGraphRef && (
                        <optgroup key="sandbox" label="Sandbox">
                          {sandboxCollections.length > 0 ? (
                            sandboxCollections.map((collection) => (
                              <option key={collection.id} value={collection.id}>
                                {collection.name}
                              </option>
                            ))
                          ) : (
                            <option
                              key="new"
                              value={NEW_DEFAULT_COLLECTION_OPTION_ID}
                            >
                              Save to a new default sandbox collection
                            </option>
                          )}
                        </optgroup>
                      )}

                      {selectedGraphRef && (
                        <optgroup key="personal" label="Personal">
                          {personalCollections.length > 0 ? (
                            personalCollections.map((collection) => (
                              <option key={collection.id} value={collection.id}>
                                {collection.name}
                              </option>
                            ))
                          ) : (
                            <option
                              key="new"
                              value={NEW_DEFAULT_COLLECTION_OPTION_ID}
                            >
                              Save to a new default personal collection
                            </option>
                          )}
                        </optgroup>
                      )}
                      {selectedGraphRef && sharedCollections.length !== 0 && (
                        <optgroup key="shared" label="Shared">
                          {sharedCollections.map((collection) => (
                            <option
                              key={collection.id}
                              value={collection.id}
                              disabled={
                                !collection.permissions.canEditOperations
                              }
                            >
                              {collection.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </Select>
                  </div>
                  {selectedCollection?.isShared &&
                    isMinLengthArray(1, selectedCollection.variants) && (
                      <Alert
                        status="info"
                        className="mt-2"
                        data-testid="shared_collection_visible"
                      >
                        Once saved, this operation will be visible to all users
                        who have access to {selectedCollection.variants[0].id}.
                      </Alert>
                    )}
                  <FormErrorMessage>
                    {errors.collectionIdOrNew}
                  </FormErrorMessage>
                </FormControl>
                {mutationErrorMessage && (
                  <ErrorMessage>{mutationErrorMessage}</ErrorMessage>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                onClick={onClose}
                type="button"
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Tooltip
                disabled={Object.entries(errors).length === 0}
                label={Object.values(errors).flatMap((error, i) => (
                  <>
                    {i !== 0 && <br />}
                    {error}
                  </>
                ))}
              >
                <Button
                  variant="primary"
                  isDisabled={
                    collectionEntryNameValidationLoading ||
                    !collections ||
                    Object.entries(errors).length !== 0
                  }
                  isLoading={isSubmitting}
                  loadingText="Save"
                  type="submit"
                >
                  Save
                </Button>
              </Tooltip>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
